import {useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {adminReturnPath} from "./adminRedirect";
import "./Admin.css";

const emptyWebinarForm = {
    title: "",
    startsAt: "",
    durationMinutes: "40",
    description: "",
    topicId: "",
    privateLink: false,
};

const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (window.location.hostname === "localhost" ? "http://localhost:8000" : "");

function apiUrl(path) {
    return `${API_BASE_URL}${path}`;
}

function authHeaders(token) {
    return {
        Authorization: `Bearer ${token}`,
    };
}

function emailReportPath(webinar, topicById, webinars) {
    const topic = topicById.get(webinar.topicId);
    const landingPageSlug = topic?.landingPageSlug || "";
    const latestTopicWebinar = webinars.find(
        (candidate) => candidate.topicId === webinar.topicId
    );
    const identifier = landingPageSlug && latestTopicWebinar?.id === webinar.id
        ? landingPageSlug
        : webinar.id;

    return `/${identifier}/emails`;
}

function formatDateTime(value) {
    if (!value) return "";

    return new Intl.DateTimeFormat("ar", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(new Date(value));
}

function CampaignList({campaigns = [], emptyMessage = "لا توجد حملات محفوظة لهذا الويبينار."}) {
    if (campaigns.length === 0) {
        return <p className="admin-empty">{emptyMessage}</p>;
    }

    const statusLabels = {
        scheduled: "مجدولة",
        skipped: "تم تخطيها",
        transactional: "ترسل عند التسجيل",
        waiting_for_recipients: "بانتظار أول تسجيل",
        sent: "تم الإرسال",
        completed: "اكتملت",
        budget_exhausted: "توقفت بسبب الحد الشهري",
    };

    return (
        <ul className="admin-campaign-list">
            {campaigns.map((campaign) => (
                <li key={campaign.key}>
                    <span>{campaign.name || campaign.label}</span>
                    <strong>{statusLabels[campaign.status] || campaign.status}</strong>
                    <small>{campaign.scheduledAt ? formatDateTime(campaign.scheduledAt) : "عند تسجيل العميل"}</small>
                </li>
            ))}
        </ul>
    );
}

export default function Admin() {
    const location = useLocation();
    const navigate = useNavigate();
    const returnTo = useMemo(() => adminReturnPath(location.search), [location.search]);
    const [token, setToken] = useState(() => localStorage.getItem("webinarAdminToken") || "");
    const [user, setUser] = useState(null);
    const [webinars, setWebinars] = useState([]);
    const [topics, setTopics] = useState([]);
    const [integrations, setIntegrations] = useState(null);
    const [message, setMessage] = useState("");
    const [isCheckingAuth, setIsCheckingAuth] = useState(Boolean(token));
    const [isLoadingWebinars, setIsLoadingWebinars] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [loginForm, setLoginForm] = useState({
        username: "",
        password: "",
    });
    const [webinarForm, setWebinarForm] = useState(emptyWebinarForm);
    const topicById = useMemo(
        () => new Map(topics.map((topic) => [topic.id, topic])),
        [topics]
    );

    const defaultTopicId = (topicOptions = topics) =>
        (topicOptions.find((topic) => topic.isValid) || {}).id || "";

    const fetchAdminWebinars = async (activeToken = token) => {
        setIsLoadingWebinars(true);

        try {
            const response = await fetch(apiUrl("/api/admin/webinars"), {
                headers: authHeaders(activeToken),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "تعذر تحميل الويبينارات.");
            }

            setWebinars(data.webinars || []);
            setTopics(data.topics || []);
            setWebinarForm((currentForm) => ({
                ...currentForm,
                topicId: currentForm.topicId || defaultTopicId(data.topics || []),
            }));
            setIntegrations(data.integrations || null);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setIsLoadingWebinars(false);
        }
    };

    useEffect(() => {
        if (!token) {
            setIsCheckingAuth(false);
            return;
        }

        const checkAuth = async () => {
            try {
                const response = await fetch(apiUrl("/api/admin/me"), {
                    headers: authHeaders(token),
                });
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.message || "يرجى تسجيل الدخول مرة أخرى.");
                }

                setUser(data.user);
                if (returnTo) {
                    navigate(returnTo, {replace: true});
                    return;
                }
                fetchAdminWebinars(token);
            } catch (error) {
                localStorage.removeItem("webinarAdminToken");
                setToken("");
                setUser(null);
                setMessage(error.message);
            } finally {
                setIsCheckingAuth(false);
            }
        };

        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, navigate, returnTo]);

    const handleLoginChange = (event) => {
        const {name, value} = event.target;

        setLoginForm((currentForm) => ({
            ...currentForm,
            [name]: value,
        }));
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setMessage("");

        try {
            const response = await fetch(apiUrl("/api/admin/login"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(loginForm),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "بيانات الدخول غير صحيحة.");
            }

            localStorage.setItem("webinarAdminToken", data.token);
            setToken(data.token);
            setUser(data.user);
            setLoginForm({username: "", password: ""});
            setMessage(data.message);

            if (returnTo) {
                navigate(returnTo, {replace: true});
            }
        } catch (error) {
            setMessage(error.message);
        }
    };

    const handleLogout = async () => {
        if (token) {
            await fetch(apiUrl("/api/admin/logout"), {
                method: "POST",
                headers: authHeaders(token),
            }).catch(() => {});
        }

        localStorage.removeItem("webinarAdminToken");
        setToken("");
        setUser(null);
        setWebinars([]);
        setIntegrations(null);
        setMessage("تم تسجيل الخروج.");
    };

    const handleWebinarChange = (event) => {
        const {name, type, value, checked} = event.target;

        setWebinarForm((currentForm) => ({
            ...currentForm,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleCreateWebinar = async (event) => {
        event.preventDefault();

        if (!webinarForm.title.trim() || !webinarForm.startsAt) {
            setMessage("يرجى إدخال عنوان الويبينار وموعده.");
            return;
        }

        if (!webinarForm.topicId) {
            setMessage("يرجى اختيار موضوع رسائل الويبينار.");
            return;
        }

        setIsCreating(true);
        setMessage("");

        try {
            const response = await fetch(apiUrl("/api/admin/webinars"), {
                method: "POST",
                headers: {
                    ...authHeaders(token),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(webinarForm),
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || "تعذر إنشاء الويبينار.");
            }

            setWebinars((currentWebinars) => [data.webinar, ...currentWebinars]);
            setWebinarForm({
                ...emptyWebinarForm,
                topicId: defaultTopicId(),
            });
            setMessage(data.message);
        } catch (error) {
            setMessage(error.message);
        } finally {
            setIsCreating(false);
        }
    };

    if (isCheckingAuth) {
        return (
            <main className="CoachAdmin">
                <div className="admin-auth-card">
                    <p className="admin-empty">جاري التحقق من جلسة الإدارة...</p>
                </div>
            </main>
        );
    }

    if (!token || !user) {
        return (
            <main className="CoachAdmin">
                <form className="admin-auth-card admin-form" onSubmit={handleLogin}>
                    <div>
                        <p className="admin-eyebrow">منطقة الإدارة</p>
                        <h1>تسجيل دخول المدير</h1>
                        <p className="admin-muted">سجل الدخول لإدارة الويبينارات.</p>
                    </div>

                    <label htmlFor="username">اسم المستخدم</label>
                    <input
                        id="username"
                        name="username"
                        type="text"
                        value={loginForm.username}
                        onChange={handleLoginChange}
                        autoComplete="username"
                    />

                    <label htmlFor="password">كلمة المرور</label>
                    <input
                        id="password"
                        name="password"
                        type="password"
                        value={loginForm.password}
                        onChange={handleLoginChange}
                        autoComplete="current-password"
                    />

                    <button type="submit">دخول</button>

                    {message && (
                        <p className="admin-message" role="status">
                            {message}
                        </p>
                    )}
                </form>
            </main>
        );
    }

    return (
        <main className="CoachAdmin">
            <div className="admin-frame">
                <header className="admin-top-bar">
                    <div>
                        <p className="admin-eyebrow">منطقة الإدارة</p>
                        <h1>إدارة الويبينارات</h1>
                        <p className="admin-muted">مسجل الدخول باسم {user.username}</p>
                    </div>

                    <button className="admin-secondary-button" type="button" onClick={handleLogout}>
                        خروج
                    </button>
                </header>

                {integrations && (
                    <div className={`admin-integration-status ${integrations.zoom?.configured ? "is-ready" : "is-missing"}`}>
                        <strong>
                            {integrations.zoom?.configured ? "إعدادات Zoom جاهزة" : "إعدادات Zoom غير مكتملة"}
                        </strong>
                        <span>
                            {integrations.zoom?.configured
                                ? `سيتم إنشاء الاجتماع باسم ${integrations.zoom.hostUserId} باستخدام خيار رابط Zoom الذي تختاره في نموذج الإنشاء.`
                                : "أضف بيانات Zoom إلى ملف البيئة في الخادم قبل إنشاء ويبينار."}
                        </span>
                        {integrations.whatsapp?.enabled && (
                            <span>
                                WhatsApp: {integrations.whatsapp.mode === "mock" ? "وضع تجريبي محلي" : "وضع حقيقي"} — أُرسلت {integrations.whatsapp.usage?.messagesSent || 0} رسالة هذا الشهر، والتكلفة التقديرية ${Number(integrations.whatsapp.usage?.estimatedCostUsd || 0).toFixed(2)} من حد ${Number(integrations.whatsapp.usage?.budgetUsd || 0).toFixed(2)}.
                            </span>
                        )}
                    </div>
                )}

                <section className="admin-grid" aria-labelledby="admin-title">
                    <form className="admin-form" onSubmit={handleCreateWebinar}>
                        <div>
                            <h2 id="admin-title">إنشاء ويبينار</h2>
                            <p className="admin-muted">
                                عند إنشاء ويبينار جديد سيتم إنشاء ملف JSON خاص بصفحة الهبوط داخل public/landingPages/webinars.
                            </p>
                        </div>

                        <label htmlFor="title">عنوان الويبينار</label>
                        <input
                            id="title"
                            name="title"
                            type="text"
                            value={webinarForm.title}
                            onChange={handleWebinarChange}
                            placeholder="مثال: إدارة الوقت"
                        />

                        <label htmlFor="startsAt">التاريخ والوقت</label>
                        <input
                            id="startsAt"
                            name="startsAt"
                            type="datetime-local"
                            value={webinarForm.startsAt}
                            onChange={handleWebinarChange}
                        />

                        <label htmlFor="durationMinutes">مدة الويبينار بالدقائق</label>
                        <input
                            id="durationMinutes"
                            name="durationMinutes"
                            type="number"
                            min="15"
                            max="120"
                            step="5"
                            value={webinarForm.durationMinutes}
                            onChange={handleWebinarChange}
                        />
                        <p className="admin-muted">
                            حساب Zoom Pro يسمح بإنشاء لقاء يصل إلى ساعتين من هذه الصفحة.
                        </p>

                        <label htmlFor="description">الوصف</label>
                        <textarea
                            id="description"
                            name="description"
                            value={webinarForm.description}
                            onChange={handleWebinarChange}
                            placeholder="وصف مختصر للويبينار"
                            rows="4"
                        />

                        <label htmlFor="topicId">موضوع رسائل الويبينار</label>
                        <select
                            id="topicId"
                            name="topicId"
                            value={webinarForm.topicId}
                            onChange={handleWebinarChange}
                            disabled={topics.length === 0}
                        >
                            <option value="">اختر موضوع الرسائل</option>
                            {topics.map((topic) => (
                                <option
                                    key={topic.id}
                                    value={topic.id}
                                    disabled={!topic.isValid}
                                >
                                    {topic.name} ({topic.id})
                                </option>
                            ))}
                        </select>
                        <p className="admin-muted">
                            يتم تحميل رسائل التأكيد والتذكير من ملف JSON داخل backend/data/webinars_topics، ويتم حفظ معرف الموضوع فقط داخل الويبينار.
                        </p>

                        <label className="admin-checkbox" htmlFor="privateLink">
                            <input
                                id="privateLink"
                                name="privateLink"
                                type="checkbox"
                                checked={Boolean(webinarForm.privateLink)}
                                onChange={handleWebinarChange}
                            />
                            <span>رابط زوم خاص (Private Zoom link)</span>
                        </label>
                        <p className="admin-muted admin-checkbox-help">
                            عند التفعيل سيرسل رابط زوم خاص لكل مشترك
                        </p>

                        <button type="submit" disabled={isCreating}>
                            {isCreating ? "جاري إنشاء الويبينار..." : "إنشاء الويبينار"}
                        </button>

                        {message && (
                            <p className="admin-message" role="status">
                                {message}
                            </p>
                        )}
                    </form>

                    <div className="admin-webinars-list">
                        <div>
                            <h2>الويبينارات</h2>
                            <p className="admin-muted">
                                كل صفحة ويبينار تفتح مباشرة من /webinar_id ويتم عرضها من ملف JSON الخاص بها.
                            </p>
                        </div>

                        {isLoadingWebinars ? (
                            <p className="admin-empty">جاري التحميل...</p>
                        ) : webinars.length === 0 ? (
                            <p className="admin-empty">لا توجد ويبينارات بعد.</p>
                        ) : (
                            webinars.map((webinar) => (
                                <article className="admin-webinar-card" key={webinar.id}>
                                    <div>
                                        <strong>{webinar.title}</strong>
                                        <span>{formatDateTime(webinar.startsAt)}</span>
                                    </div>
                                    {webinar.description && <p>{webinar.description}</p>}
                                    <dl>
                                        <dt>المعرف</dt>
                                        <dd>{webinar.id}</dd>
                                        <dt>الصفحة</dt>
                                        <dd>
                                            <a href={`/${webinar.id}`} target="_blank" rel="noreferrer">
                                                /{webinar.id}
                                            </a>
                                        </dd>
                                        <dt>موضوع الرسائل</dt>
                                        <dd>
                                            {topicById.get(webinar.topicId)?.name || webinar.topicId || "غير محدد"}
                                        </dd>
                                        <dt>نوع رابط Zoom</dt>
                                        <dd>
                                            {webinar.zoom?.registrationRequired
                                                ? "خاص — رابط فردي بعد التسجيل"
                                                : "مشترك — يمكن استخدام الرابط مباشرة"}
                                        </dd>
                                        <dt>تقرير التسجيل</dt>
                                        <dd>
                                            <a href={emailReportPath(webinar, topicById, webinars)}>
                                                عرض التقرير
                                            </a>
                                        </dd>
                                        <dt>قائمة Brevo</dt>
                                        <dd>{webinar.brevoListName || webinar.brevoListId || "غير متاحة"}</dd>
                                        <dt>دخول الحضور إلى Zoom</dt>
                                        <dd>
                                            {webinar.zoom?.registrationRequired ? (
                                                "يُرسل رابط فردي قابل للمشاركة بعد التسجيل، ولا يتطلب تسجيل دخول إلى Zoom."
                                            ) : webinar.zoom?.joinUrl ? (
                                                <a href={webinar.zoom.joinUrl} target="_blank" rel="noreferrer">
                                                    {webinar.zoom.joinUrl}
                                                </a>
                                            ) : (
                                                "غير متاح"
                                            )}
                                        </dd>
                                        {webinar.zoom?.meetingId && (
                                            <>
                                                <dt>معرف Zoom</dt>
                                                <dd>{webinar.zoom.meetingId}</dd>
                                            </>
                                        )}
                                        {webinar.zoom?.passcode && (
                                            <>
                                                <dt>رمز الدخول</dt>
                                                <dd>{webinar.zoom.passcode}</dd>
                                            </>
                                        )}
                                    </dl>
                                    <CampaignList campaigns={webinar.brevoCampaigns || []}/>
                                    <h3 className="admin-campaign-heading">حملات WhatsApp</h3>
                                    <CampaignList
                                        campaigns={webinar.whatsappCampaigns || []}
                                        emptyMessage="لا توجد حملات WhatsApp محفوظة لهذا الويبينار."
                                    />
                                </article>
                            ))
                        )}
                    </div>
                </section>
            </div>
        </main>
    );
}
